import React, { useState } from 'react';
import contactsService from '../../services/contacts';
import { useToast } from '../../hooks/useToast';

const ContactCreateDrawer = ({ onClose, onContactCreated }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    cuil: '',
    email: '',
    phone: '',
    phone2: '',
    address: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { success, error } = useToast();

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Limpiar error del campo cuando se modifica
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'El nombre es requerido';
    }

    if (!formData.cuil.trim()) {
      newErrors.cuil = 'El CUIL es requerido';
    } else if (!contactsService.validateCuitCuil(formData.cuil)) {
      newErrors.cuil = 'El CUIL debe tener exactamente 11 dígitos';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!contactsService.validateEmail(formData.email)) {
      newErrors.email = 'El email no tiene un formato válido';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'El teléfono es requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const contactPayload = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        cuil: formData.cuil.replace(/\D/g, ''),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        phone2: formData.phone2.trim(),
        address: formData.address.trim()
      };

      const response = await contactsService.createContact(contactPayload);
      
      // Crear objeto de contacto con los datos del formulario para autoseleccionar
      const createdContact = {
        id: response.id,
        first_name: contactPayload.first_name,
        last_name: contactPayload.last_name,
        cuil: contactPayload.cuil,
        email: contactPayload.email,
        phone: contactPayload.phone,
        phone2: contactPayload.phone2,
        address: contactPayload.address
      };
      
      onContactCreated(createdContact);
      success('Contacto creado exitosamente');
      
    } catch (err) {
      console.error('Error al crear contacto:', err);
      
      if (err.type === 'validation' && err.errors) {
        // Errores de validación 422 - mapear por campo
        const backendErrors = {};
        Object.keys(err.errors).forEach(field => {
          backendErrors[field] = err.errors[field];
        });
        setErrors(backendErrors);
        
        // Enfocar el primer campo con error
        const firstErrorField = Object.keys(backendErrors)[0];
        if (firstErrorField) {
          setTimeout(() => {
            const element = document.querySelector(`[name="${firstErrorField}"], #${firstErrorField}`);
            if (element) {
              element.focus();
            }
          }, 100);
        }
        
        error('Por favor corrija los errores en el formulario');
      } else if (err.response?.status === 403) {
        // Backend no disponible - el servicio ya maneja esto con mock
        console.warn('Backend no disponible, pero el contacto se creó en modo mock');
        // No mostrar error, el servicio ya devolvió un ID mock
      } else {
        // Otros errores
        const errorMessage = err.message || 
          (err.response?.status === 500 
            ? 'Error interno del servidor. Intente nuevamente.'
            : 'Error al crear el contacto. Intente nuevamente.');
        error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-create-drawer-overlay" onClick={onClose}>
      <div className="contact-create-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-header">
          <h3>Crear nuevo contacto</h3>
          <button type="button" className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="drawer-form">
            <div className="form-group">
              <label htmlFor="cuil">CUIL *</label>
              <input
                type="text"
                id="cuil"
                value={formData.cuil}
                onChange={(e) => handleInputChange('cuil', e.target.value.replace(/\D/g, ''))}
                className={errors.cuil ? 'error' : ''}
                placeholder="12345678901"
                maxLength={11}
              />
              {errors.cuil && <span className="error-message">{errors.cuil}</span>}
            </div>

          <div className="form-row-2">
            <div className="form-group">
              <label htmlFor="first_name">Nombre *</label>
              <input
                type="text"
                id="first_name"
                value={formData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                className={errors.first_name ? 'error' : ''}
                placeholder="Ingrese nombre"
              />
              {errors.first_name && <span className="error-message">{errors.first_name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="last_name">Apellido</label>
              <input
                type="text"
                id="last_name"
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                className={errors.last_name ? 'error' : ''}
                placeholder="Ingrese apellido"
              />
              {errors.last_name && <span className="error-message">{errors.last_name}</span>}
            </div>
          </div>

          <div className="form-row-2">

            <div className="form-group">
              <label htmlFor="phone">Teléfono 1 *</label>
              <input
                type="text"
                id="phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={errors.phone ? 'error' : ''}
                placeholder="011-1234-5678"
              />
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="phone2">Teléfono 2</label>
              <input
                type="text"
                id="phone2"
                value={formData.phone2}
                onChange={(e) => handleInputChange('phone2', e.target.value)}
                className={errors.phone2 ? 'error' : ''}
                placeholder="011-9876-5432"
              />
              {errors.phone2 && <span className="error-message">{errors.phone2}</span>}
            </div>
          </div>

          <div className="form-row-2">
            <div className="form-group">
              <label htmlFor="email">Correo *</label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={errors.email ? 'error' : ''}
                placeholder="ejemplo@correo.com"
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="address">Dirección</label>
              <input
                type="text"
                id="address"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                className={errors.address ? 'error' : ''}
                placeholder="Ingrese dirección"
              />
              {errors.address && <span className="error-message">{errors.address}</span>}
            </div>
          </div>

          <div className="drawer-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creando...' : 'Crear Contacto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactCreateDrawer;
